import { inject, observer } from 'mobx-react';
import React, { useEffect } from 'react';
import ReactGA from 'react-ga';
import { useKeycloak } from '@react-keycloak/web';
import { initI18n } from 'translation/i18n';

import history from 'helpers/routerHistory';
import compose from 'helpers/compose';
import { Permission } from 'helpers/permissions';
import FullscreenLoader from 'components/Loading/Fullscreen';
import AppRoutes from './AppRoutes';
import ClientAppRoutes from './ClientAppRoutes';
import { UserStoreKC } from 'stores/userStoreKC';
import { UIStore } from 'stores/uiStore';
import { APP_CONFIG } from 'appConfig';

//import { Theme } from '@material-ui/core/styles/createTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import ClientError from 'pages/Error/ClientError';
import { userName } from 'helpers/keycloak';

const csDict = require('translation/lang/main/cs.json');
const enDict = require('translation/lang/main/en.json');

interface Props {
  userStore: UserStoreKC;
  uiStore: UIStore;
}

const App: React.FC<Props> = ({ userStore, uiStore }) => {
  const { loggedUser, clientError, checkPermissions, logout, loading } = userStore;
  const gaTrackingId = APP_CONFIG.gaTrackingId;

  const { keycloak, initialized } = useKeycloak();

  keycloak.onReady = () => {
    initI18n({
      cs: csDict,
      en: enDict,
    });
    userStore.getUserInfo();
  };

  //const tablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'), { noSsr: true });
  const tablet = useMediaQuery('@media (max-width: 1200px)', { noSsr: true });

  useEffect(() => {
    uiStore.setDetailMenuOpen(!tablet);
  });

  useEffect(() => {
    (async () => {
      if (initialized) {
        if (!keycloak.authenticated) {
          console.warn('Keycloak not authenticated!');
          userStore.clearMaxLoginTime();
          const url = window.location.href.split('?');
          keycloak.login(userName ? { redirectUri: url[0], loginHint: userName } : undefined);

        } else {
          console.warn('Keycloak authenticated!');
          uiStore.fetchVersion();
        }
      }
    })();
  }, [keycloak, keycloak.authenticated, initialized, userStore, uiStore]);

  if (userStore.loggedUser && !checkPermissions(Permission.APP_ADVISOR) && !checkPermissions(Permission.APP_ENDUSER)) {
    console.warn('logged user:', JSON.stringify(userStore.loggedUser, null, 2));
    console.warn('checkPermissions failed! Logging out...');
    logout();
  }

  if (loggedUser && checkPermissions(Permission.APP_ADVISOR)) {
    const trackGaPageview = (path: string) => {
      ReactGA.set({ page: path });
      ReactGA.pageview(path);
    };
    if (gaTrackingId) {
      ReactGA.initialize(gaTrackingId);
      trackGaPageview(history.location.pathname);
    }
    require('helpers/chat.js');
  }

  if (clientError) {
    return <ClientError />;
  }

  return (
    <>
      {!loading ? (
        <>
          {checkPermissions(Permission.APP_ADVISOR) && (
              <AppRoutes />
          )}
          {checkPermissions(Permission.APP_ENDUSER) && <ClientAppRoutes />}
        </>
      ) : (
        <FullscreenLoader />
      )}
    </>
  );
};

export default compose(inject('userStore', 'uiStore'), observer)(App);
